import { useCallback, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useMutation } from '@apollo/client';

import { ErrorMessageTexts, useApiResponse, useAuth } from '@libs/hooks';
import { TwoFactorAuthType } from '@libs/types';

import {
  sendRegistrationCodeMudation,
  registerUserMutation,
  RegisterUserArgs,
  RegisterUser,
} from 'src/gql';
import {
  useAppDisclaimer,
  useJurisdictionDetector,
  useTranslation,
} from 'src/hooks';
import { ClientPlatformEnum, JurisdictionsEnum } from '@libs/backend-common';
import { RegexValidators } from '@libs/validations';
import { hashString } from '@libs/helpers';

import { getUserDeviceTokenKey } from 'src/helpers';

const url = new URL(window.location.href);

const searchParams = new URLSearchParams(url.search);

const referrerCode = searchParams.get('code') || undefined;

const emailOrPhone = searchParams.get('email') || '';

export const DEFAULT_REGISTER_VALUES: RegisterUserArgs = {
  agreedToTerms: true,
  confirmationCode: '',
  password: '',
  referrerCode,
  emailOrPhone,
};
const additionalRoute = '?isNewlyRegistered=true';

export const useRegister = (
  handleSendCodeSucess: () => void,
  values: RegisterUserArgs
) => {
  const [args, setArgs] = useState<RegisterUserArgs>(DEFAULT_REGISTER_VALUES);

  const { t } = useTranslation();

  const navigate = useNavigate();
  const handleLogin = () => navigate(`/${additionalRoute}`, { replace: true });

  const { login } = useAuth(undefined, handleLogin);

  const { setIsAppDisclaimerVisible } = useAppDisclaimer();

  const { currentJurisdiction } = useJurisdictionDetector();

  const isJurisdictionGe = currentJurisdiction === JurisdictionsEnum.GE;
  const isNotMobileApp = !window.isFromMobileApp;

  const handleRegisterUserSuccess = async (data: RegisterUser) => {
    login(data.registerUser.accessToken);

    if (isJurisdictionGe && isNotMobileApp) {
      setIsAppDisclaimerVisible(true);
    }

    const userDeviceToken = data.registerUser.userDeviceToken;
    const identifier = values.emailOrPhone;

    if (userDeviceToken && identifier) {
      const hashedValue = await hashString(identifier);
      const fullKey = getUserDeviceTokenKey(hashedValue);
      const existingToken = localStorage.getItem(fullKey);

      if (!existingToken) {
        localStorage.setItem(fullKey, userDeviceToken);
      }
    }
  };

  const [sendCode, sendCodeRes] = useMutation(sendRegistrationCodeMudation);
  const [registerUser, registerUserRes] = useMutation(registerUserMutation);

  const { errorMessage: registerErrorMessage, loading } = useApiResponse(
    registerUserRes,
    handleRegisterUserSuccess
  );
  const {
    clearErrorMessage: clearSendCodeErrorMessage,
    response,
    loading: sendCodeLoading,
    errorMessage: sendCodeErrorMessage,
  } = useApiResponse(sendCodeRes, handleSendCodeSucess);

  const handleAuthComplete = useCallback(
    (_: TwoFactorAuthType, val: string) => {
      registerUser({
        variables: {
          record: {
            ...args,
            confirmationCode: val,
            clientPlatform: ClientPlatformEnum.WEB,
          },
        },
      });
    },
    [args, registerUser]
  );

  const handleFormSubmit = useCallback(
    async (vals: RegisterUserArgs) => {
      setArgs(vals);

      const { emailOrPhone, referrerCode } = vals;

      const formatedEmailOrPhone = emailOrPhone.replace(
        RegexValidators.HAS_GEORGIAN_PHONE_PREFIX,
        ''
      );

      await sendCode({
        variables: {
          record: {
            emailOrPhone: formatedEmailOrPhone,
            referrerCode,
          },
        },
      });
    },

    [sendCode]
  );

  const errorMessage = registerErrorMessage ? t(registerErrorMessage) : '';

  const isRefererCodeError = [
    ErrorMessageTexts.REFERRER_CODE_DOES_NOT_EXIST,
    ErrorMessageTexts.REFERRER_IS_NOT_VERIFIED,
  ].includes(sendCodeErrorMessage as ErrorMessageTexts);

  const refererCodeInputErrorMessage = isRefererCodeError
    ? sendCodeErrorMessage
    : '';

  const emailOrPhoneInputErrorMessage = !isRefererCodeError
    ? sendCodeErrorMessage
    : '';

  return {
    handleAuthComplete,
    response,
    handleFormSubmit,
    errorMessage,
    sendCodeLoading,
    loading,
    refererCodeInputErrorMessage,
    emailOrPhoneInputErrorMessage,
    clearSendCodeErrorMessage,
    referrerCode,
    registerUserRes,
  };
};
